/*
 * =============================================================================
 * FILE HEADER — packages/healing/src/screen.ts
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)      | Who               | Why              | What Changed                |
 * |---|------------------------|-------------------|------------------|-----------------------------|
 * | 1 | 2026-06-17 (Phase 2)   | Claude Sonnet 4.6 | Healing core     | Created — screen gate       |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - packages/healing/src/fingerprint.ts  (computeScreenSignature)
 * - packages/healing/src/heal.ts         (sameScreenGate)
 * - packages/healing/src/index.test.ts   (unit tests)
 *
 * FUNCTIONS / EXPORTS COVERED
 * ----------------------------
 * computeScreenSignature(nodes) → string
 *   Sorted pipe-separated visible text labels from all nodes.
 *   Stored in Fingerprint.screenSignature at record time.
 *
 * sameScreenGate(currentNodes, fingerprint, threshold?) → boolean
 *   Jaccard similarity of current text set vs recorded set ≥ threshold (0.35).
 *   Blocks Tier-1 healing when the user has navigated to a completely different screen.
 *
 * CALLER → CALLEE MAP
 * --------------------
 * fingerprint.ts → computeScreenSignature
 * heal.ts        → sameScreenGate → computeScreenSignature → jaccardFromSignatures
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. screenSignature is NOT a cryptographic hash — it is a sorted, pipe-separated
 *    string of visible text tokens. This allows token-level Jaccard comparison
 *    between the recorded screen and the current screen.
 * 2. The Jaccard threshold of 0.35 is intentionally lenient: nav bars and headers
 *    (Home, Back, Menu) are shared across screens and push the baseline Jaccard
 *    above 0 even for different content screens. 0.35 lets healing work on
 *    same-section screens that differ only in list content.
 * 3. Empty tokens (whitespace-only) and duplicate texts are deduplicated.
 *    The same text appearing on multiple nodes still counts as one token in
 *    the set — Jaccard is a set operation, not a multiset operation.
 *
 * ISSUES FACED
 * ------------
 * None
 *
 * =============================================================================
 */
const DEFAULT_JACCARD_THRESHOLD = 0.35;
// ── computeScreenSignature ────────────────────────────────────────────────────
/**
 * Build a stable string representation of the visible text set on screen.
 * Collects all non-empty text and contentDesc values, deduplicates, sorts,
 * and joins with "|".
 *
 * The resulting string can be compared between two snapshots via Jaccard
 * similarity to detect same-vs-different screen context.
 */
export function computeScreenSignature(nodes) {
    const texts = new Set();
    for (const node of nodes) {
        const t = node.text?.trim();
        const cd = node.contentDesc?.trim();
        if (t && t.length > 0)
            texts.add(t.toLowerCase());
        if (cd && cd.length > 0)
            texts.add(cd.toLowerCase());
    }
    return Array.from(texts).sort().join("|");
}
// ── jaccardFromSignatures ─────────────────────────────────────────────────────
/**
 * Compute Jaccard similarity between two pipe-separated screen signatures.
 * Jaccard = |A ∩ B| / |A ∪ B|
 */
function jaccardFromSignatures(sigA, sigB) {
    if (sigA === sigB)
        return 1; // fast path for identical screens
    if (!sigA && !sigB)
        return 1;
    if (!sigA || !sigB)
        return 0;
    const setA = new Set(sigA.split("|").filter(Boolean));
    const setB = new Set(sigB.split("|").filter(Boolean));
    let intersectionCount = 0;
    for (const item of setA) {
        if (setB.has(item))
            intersectionCount++;
    }
    const unionCount = setA.size + setB.size - intersectionCount;
    return unionCount === 0 ? 1 : intersectionCount / unionCount;
}
// ── sameScreenGate ────────────────────────────────────────────────────────────
/**
 * Returns true when the current screen's visible text set overlaps enough
 * with the fingerprint's recorded screen to make healing meaningful.
 *
 * A Jaccard < threshold means the user has navigated to an unrelated screen —
 * any element found there would be a false positive.
 */
export function sameScreenGate(currentNodes, fingerprint, threshold = DEFAULT_JACCARD_THRESHOLD) {
    const currentSig = computeScreenSignature(currentNodes);
    const jaccard = jaccardFromSignatures(currentSig, fingerprint.screenSignature);
    return jaccard >= threshold;
}
/** Exported for testing: compute Jaccard directly from two pre-built signatures. */
export function jaccardOfSignatures(sigA, sigB) {
    return jaccardFromSignatures(sigA, sigB);
}
//# sourceMappingURL=screen.js.map